import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MapPin, Plus, Pencil, Trash2, Star } from 'lucide-react';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Badge from '@/components/ui/Badge';

interface Address {
  id: string;
  first_name: string;
  last_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  phone: string;
  is_default_shipping: boolean;
  is_default_billing: boolean;
}

const emptyAddress: Omit<Address, 'id' | 'is_default_shipping' | 'is_default_billing'> = {
  first_name: '',
  last_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  phone: '',
};

const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;

function useAddresses() {
  return useQuery({
    queryKey: ['addresses'],
    queryFn: () =>
      api.get<{ addresses: Address[] }>('/user/addresses'),
  });
}

export default function Addresses() {
  const { t } = useTranslation('account');
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useAddresses();
  const addToast = useUIStore((s) => s.addToast);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyAddress);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const addresses = data?.addresses ?? [];

  const stateOptions = useMemo(
    () => US_STATE_CODES.map((code) => ({ value: code, label: t(`addresses.states.${code}`) })),
    [t],
  );

  // Populate form when editing
  useEffect(() => {
    if (editingId) {
      const addr = addresses.find((a) => a.id === editingId);
      if (addr) {
        setForm({
          first_name: addr.first_name,
          last_name: addr.last_name,
          address_line1: addr.address_line1,
          address_line2: addr.address_line2 || '',
          city: addr.city,
          state: addr.state,
          postal_code: addr.postal_code,
          phone: addr.phone || '',
        });
      }
    }
  }, [editingId, addresses]);

  function openAddModal() {
    setEditingId(null);
    setForm(emptyAddress);
    setErrors({});
    setIsModalOpen(true);
  }

  function openEditModal(id: string) {
    setEditingId(id);
    setErrors({});
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingId(null);
    setForm(emptyAddress);
    setErrors({});
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.first_name.trim()) newErrors.first_name = t('addresses.errors.firstNameRequired');
    if (!form.last_name.trim()) newErrors.last_name = t('addresses.errors.lastNameRequired');
    if (!form.address_line1.trim()) newErrors.address_line1 = t('addresses.errors.addressRequired');
    if (!form.city.trim()) newErrors.city = t('addresses.errors.cityRequired');
    if (!form.state) newErrors.state = t('addresses.errors.stateRequired');
    if (!form.postal_code.trim()) newErrors.postal_code = t('addresses.errors.postalCodeRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setIsSaving(true);
    try {
      if (editingId) {
        await api.put(`/user/addresses/${editingId}`, form);
        addToast(t('addresses.toasts.updated'), 'success');
      } else {
        await api.post('/user/addresses', form);
        addToast(t('addresses.toasts.added'), 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      closeModal();
    } catch (err: any) {
      addToast(err.message || t('addresses.toasts.saveFailed'), 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await api.delete(`/user/addresses/${id}`);
      addToast(t('addresses.toasts.deleted'), 'success');
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    } catch (err: any) {
      addToast(err.message || t('addresses.toasts.deleteFailed'), 'error');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSetDefault(id: string, type: 'shipping' | 'billing') {
    try {
      await api.put(`/user/addresses/${id}/default`, { type });
      const typeLabel =
        type === 'shipping'
          ? t('addresses.toasts.typeShipping')
          : t('addresses.toasts.typeBilling');
      addToast(t('addresses.toasts.defaultUpdated', { type: typeLabel }), 'success');
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    } catch (err: any) {
      addToast(err.message || t('addresses.toasts.defaultUpdateFailed'), 'error');
    }
  }

  function updateForm(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">
          {t('addresses.errorLoading')}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('addresses.title')}
        </h2>
        <Button size="sm" onClick={openAddModal}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('addresses.addAddress')}
        </Button>
      </div>

      {addresses.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <MapPin
              className="h-12 w-12 text-ink-muted mx-auto mb-3"
              aria-hidden="true"
            />
            <p className="text-ink-muted mb-4">
              {t('addresses.empty')}
            </p>
            <Button onClick={openAddModal}>{t('addresses.addFirst')}</Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address) => {
            const fullName = `${address.first_name} ${address.last_name}`;
            return (
              <Card key={address.id} className="relative">
                {/* Default badges */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {address.is_default_shipping && (
                    <Badge variant="green">
                      <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                      {t('addresses.defaultShipping')}
                    </Badge>
                  )}
                  {address.is_default_billing && (
                    <Badge variant="blue">
                      <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                      {t('addresses.defaultBilling')}
                    </Badge>
                  )}
                </div>

                <address className="text-sm text-ink-soft not-italic leading-relaxed mb-4">
                  <span className="font-semibold">
                    {fullName}
                  </span>
                  <br />
                  {address.address_line1}
                  {address.address_line2 && (
                    <>
                      <br />
                      {address.address_line2}
                    </>
                  )}
                  <br />
                  {address.city}, {address.state} {address.postal_code}
                  {address.phone && (
                    <>
                      <br />
                      {address.phone}
                    </>
                  )}
                </address>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 border-t border-line pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(address.id)}
                    aria-label={t('addresses.editAriaLabel', { name: fullName })}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('addresses.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(address.id)}
                    isLoading={deletingId === address.id}
                    aria-label={t('addresses.deleteAriaLabel', { name: fullName })}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('addresses.delete')}
                  </Button>
                  {!address.is_default_shipping && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(address.id, 'shipping')}
                    >
                      {t('addresses.setDefaultShipping')}
                    </Button>
                  )}
                  {!address.is_default_billing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(address.id, 'billing')}
                    >
                      {t('addresses.setDefaultBilling')}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Address Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingId ? t('addresses.modal.editTitle') : t('addresses.modal.addTitle')}
        className="max-w-xl"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          noValidate
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('addresses.form.firstName')}
                value={form.first_name}
                onChange={(e) => updateForm('first_name', e.target.value)}
                error={errors.first_name}
                required
                autoComplete="given-name"
              />
              <Input
                label={t('addresses.form.lastName')}
                value={form.last_name}
                onChange={(e) => updateForm('last_name', e.target.value)}
                error={errors.last_name}
                required
                autoComplete="family-name"
              />
            </div>
            <Input
              label={t('addresses.form.addressLine1')}
              value={form.address_line1}
              onChange={(e) => updateForm('address_line1', e.target.value)}
              error={errors.address_line1}
              required
              autoComplete="address-line1"
            />
            <Input
              label={t('addresses.form.addressLine2')}
              value={form.address_line2}
              onChange={(e) => updateForm('address_line2', e.target.value)}
              helperText={t('addresses.form.addressLine2Help')}
              autoComplete="address-line2"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Input
                label={t('addresses.form.city')}
                value={form.city}
                onChange={(e) => updateForm('city', e.target.value)}
                error={errors.city}
                required
                autoComplete="address-level2"
              />
              <Select
                label={t('addresses.form.state')}
                value={form.state}
                onChange={(e) => updateForm('state', e.target.value)}
                options={stateOptions}
                placeholder={t('addresses.form.statePlaceholder')}
                error={errors.state}
                required
              />
              <Input
                label={t('addresses.form.postalCode')}
                value={form.postal_code}
                onChange={(e) => updateForm('postal_code', e.target.value)}
                error={errors.postal_code}
                required
                autoComplete="postal-code"
                inputMode="numeric"
              />
            </div>
            <Input
              label={t('addresses.form.phone')}
              type="tel"
              value={form.phone}
              onChange={(e) => updateForm('phone', e.target.value)}
              helperText={t('addresses.form.phoneHelp')}
              autoComplete="tel"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line">
            <Button type="button" variant="ghost" onClick={closeModal}>
              {t('addresses.modal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSaving}>
              {editingId ? t('addresses.modal.updateAction') : t('addresses.modal.addAction')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
